import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useRef, useState } from "react";
import type { RechenmauerExerciseConfig, RechenmauerRunSettings } from "@calega/content";
import { rechenmauerCellKey, type RechenmauerFieldEvent, type RechenmauerRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Feedback = "" | "correct" | "wrong";

interface State {
  runId: string;
  run: RechenmauerRun;
  index: number;
  phase: "running" | "complete";
  values: Record<string, number | null>;
  events: RechenmauerFieldEvent[];
  submitAttempts: number;
  feedback: Feedback;
  result: { score: number; maxScore: number } | null;
}

const standardCounts = [5, 10, 15, 20];
const standardMaximums = [10, 20, 50, 100, 200, 1_000, 100_000];

function optionsWithDefault(defaultValue: number, options: number[]) {
  return [...new Set([defaultValue, ...options])].sort((left, right) => left - right);
}

export function canAdvanceRechenmauer(feedback: Feedback, canControl: boolean) {
  return feedback === "correct" && canControl;
}

export function Rechenmauer({ account, config, pageId }: { account: Account; config: RechenmauerExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const inSyncSession = Boolean(realtime.session);
  const follower = inSyncSession && !facilitator;
  const canControl = !inSyncSession || facilitator;
  const canAnswer = !inSyncSession || facilitator || follower;
  const [count, setCount] = useState(config.count);
  const [height, setHeight] = useState<3 | 4>(config.height);
  const [maxResult, setMaxResult] = useState(config.maxResult);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const sequence = useRef(0);

  function publish(state: State | null) {
    realtime.publishExercise({ exerciseType: "rechenmauer", pageId, state });
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: State | null } | null;
    if (synced?.exerciseType === "rechenmauer" && synced.pageId === pageId) {
      setExercise(synced.state ?? null);
    }
  }, [realtime.state.exercise, pageId]);

  function settings(): RechenmauerRunSettings {
    return { count, height, maxResult };
  }

  async function createRun(runSettings: RechenmauerRunSettings) {
    const response = await startExerciseRun<{ runId: string; exercise: RechenmauerRun }>({ contentPageId: pageId, settings: runSettings });
    const next: State = {
      runId: response.runId,
      run: response.exercise,
      index: 0,
      phase: "running",
      values: {},
      events: [],
      submitAttempts: 0,
      feedback: "",
      result: null,
    };
    sequence.current = 0;
    setExercise(next);
    setMessage("");
    publish(next);
  }

  async function start(runSettings = settings()) {
    if (busy || !canControl) return;
    setBusy(true);
    try {
      await createRun(runSettings);
    } finally {
      setBusy(false);
    }
  }

  async function abort() {
    if (exercise && exercise.phase !== "complete") {
      await abortExerciseRun(exercise.runId);
    }
  }

  async function back() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abort();
      setCount(exercise.run.settings.count);
      setHeight(exercise.run.settings.height);
      setMaxResult(exercise.run.settings.maxResult);
      setExercise(null);
      setMessage("");
      publish(null);
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const runSettings = exercise.run.settings;
    setBusy(true);
    try {
      await abort();
      await createRun(runSettings);
    } finally {
      setBusy(false);
    }
  }

  function change(cellKey: string, rawValue: string) {
    if (!exercise || exercise.phase !== "running" || exercise.feedback === "correct" || busy || !canAnswer) return;
    const parsed = Number(rawValue);
    const value = rawValue === "" || !Number.isInteger(parsed) ? null : parsed;
    const nextSequence = Math.max(sequence.current, ...exercise.events.map((event) => event.seq), 0) + 1;
    sequence.current = nextSequence;
    const wall = exercise.run.walls[exercise.index];
    const next: State = {
      ...exercise,
      values: { ...exercise.values, [cellKey]: value },
      events: wall ? [...exercise.events, { seq: nextSequence, wallId: wall.id, cellKey, value }] : exercise.events,
      feedback: "",
    };
    setExercise(next);
    setMessage("");
    publish(next);
  }

  async function check() {
    if (!exercise || exercise.phase !== "running" || exercise.feedback === "correct" || busy || !canAnswer) return;
    const wall = exercise.run.walls[exercise.index];
    if (!wall) return;
    setBusy(true);
    try {
      const attempts = exercise.submitAttempts + 1;
      const response = await submitExerciseAnswer<{ answer: { correct: boolean } }>(exercise.runId, {
          type: "rechenmauer",
          wallId: wall.id,
          values: exercise.values,
          events: exercise.events,
          submitAttempts: attempts,
        });
      const next: State = {
        ...exercise,
        submitAttempts: attempts,
        feedback: response.answer.correct ? "correct" : "wrong",
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function advance() {
    if (!exercise || busy || exercise.phase !== "running" || !canAdvanceRechenmauer(exercise.feedback, canControl)) return;
    if (exercise.index + 1 >= exercise.run.walls.length) {
      await completion.complete(exercise.runId, (result) => {
        const next: State = { ...exercise, result, phase: "complete" };
        setExercise(next);
        setMessage("");
        publish(next);
      });
      return;
    }
    const next: State = {
      ...exercise,
      index: exercise.index + 1,
      values: {},
      events: [],
      submitAttempts: 0,
      feedback: "",
    };
    setExercise(next);
    setMessage("");
    publish(next);
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await back();
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="rechenmauer-exercise" />;

  if (!exercise) {
    return (
      <section className="rechenmauer-exercise">
        <p className="quiz-instructions learning-text">{config.instructions}</p>
        <div className="mathe-settings rechenmauer-settings">
          <fieldset>
            <legend>Höhe der Rechenmauer</legend>
            <div className="mathe-option-grid">
              {([3, 4] as const).map((value) => (
                <label className="checkbox-label" key={value}>
                  <input type="radio" name={`${pageId}-wall-height`} checked={height === value} onChange={() => setHeight(value)} />
                  {value} Reihen
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Zahlenbereich</legend>
            <label>
              Maximales Ergebnis
              <select value={maxResult} onChange={(event) => setMaxResult(Number(event.target.value))}>
                {optionsWithDefault(config.maxResult, standardMaximums).map((value) => (
                  <option value={value} key={value}>bis {value.toLocaleString("de-DE")}</option>
                ))}
              </select>
            </label>
          </fieldset>
          <fieldset>
            <legend>Durchlauf</legend>
            <label>
              Anzahl Rechenmauern
              <select value={count} onChange={(event) => setCount(Number(event.target.value))}>
                {optionsWithDefault(config.count, standardCounts).map((value) => <option value={value} key={value}>{value}</option>)}
              </select>
            </label>
          </fieldset>
        </div>
        <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>
          Rechenmauern starten
        </button>
        {message && <div className="alert info">{message}</div>}
      </section>
    );
  }

  const wall = exercise.run.walls[exercise.index];
  const last = exercise.index + 1 >= exercise.run.walls.length;

  return (
    <section className="rechenmauer-exercise">
      {canControl && exercise.phase !== "complete" && (
        <ExerciseMenu>
          <button className="secondary small" disabled={busy} onClick={() => back().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
          <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
        </ExerciseMenu>
      )}
      <div className="exercise-progress">Mauer {Math.min(exercise.index + 1, exercise.run.walls.length)} von {exercise.run.walls.length}</div>
      {exercise.phase === "complete" ? (
        <>
          <div className="reime-result">
            <strong>{exercise.result?.score ?? exercise.run.walls.length} von {exercise.result?.maxScore ?? exercise.run.walls.length} Rechenmauern richtig</strong>
            <p>Der Durchlauf ist abgeschlossen.</p>
          </div>
          {canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
          {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
        </>
      ) : wall ? (
        <>
          <p className="quiz-instructions learning-text">{config.instructions}</p>
          <div className="number-wall" aria-label={`Rechenmauer ${exercise.index + 1}`}>
            {wall.rows.map((_, reverseIndex) => wall.rows.length - 1 - reverseIndex).map((rowIndex) => (
              <div className="wall-row" key={rowIndex}>
                {wall.rows[rowIndex]!.map((expected, cellIndex) => {
                  const key = rechenmauerCellKey(rowIndex, cellIndex);
                  if (rowIndex === 0) return <div className="wall-cell fixed" key={key}>{expected}</div>;
                  return (
                    <input
                      className={`wall-cell ${exercise.feedback && exercise.values[key] !== expected ? "wrong" : exercise.feedback === "correct" ? "correct" : ""}`}
                      aria-label={`Reihe ${rowIndex + 1}, Feld ${cellIndex + 1}`}
                      inputMode="numeric"
                      type="number"
                      step="1"
                      value={exercise.values[key] ?? ""}
                      disabled={busy || !canAnswer || exercise.feedback === "correct"}
                      key={key}
                      onChange={(event) => change(key, event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter") check().catch((error) => setMessage(error.message)); }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          {exercise.feedback === "wrong" && <div className="answer-feedback wrong">Noch nicht vollständig richtig. Prüfe die markierten Steine.</div>}
          {exercise.feedback === "correct" && <div className="answer-feedback correct">Richtig – die Mauer stimmt vollständig.</div>}
          <div className="inline-actions wall-actions">
            {canAdvanceRechenmauer(exercise.feedback, canControl) ? (
              <button className="primary" disabled={busy} onClick={() => advance().catch((error) => setMessage(error.message))}>
                {last ? "Auswertung ansehen" : "Nächste Mauer"}
              </button>
            ) : exercise.feedback === "correct" && follower ? (
              <span className="muted">Die nächste Mauer wird von der Sitzungsleitung geöffnet.</span>
            ) : (
              <button className="primary" disabled={busy || !canAnswer} onClick={() => check().catch((error) => setMessage(error.message))}>
                Mauer prüfen
              </button>
            )}
          </div>
        </>
      ) : null}
      {message && <div className="alert info">{message}</div>}
    </section>
  );
}
